// Security settings strings — EN + TR.
//
// Wave-2 Day 1. 2FA + active sessions list + API tokens placeholder cards.
// Volkan direction 2026-05-23 (Settings 10-section expansion item #9).
//
// Session metadata format `Chrome · macOS · Austin, TX` invariant — separator
// `·` + city/region subject preserved EN proper noun (data-driven; locale
// tercihi sadece chrome'u etkiler, session subject preserve eder).
//
// Source: features/settings/Security.tsx.

import type { LocalizedDict } from '../index'

type SecurityStrings = {
  pageTitle: string
  pageDescription: string
  footerNote: string
  toastTail: string
  twoFactor: {
    title: string
    description: string
    disabledBadge: string
    enableAction: string
    enableLabel: string
  }
  sessions: {
    title: string
    description: string
    activeBadge: string
    currentSessionMeta: string
    lastActive: (relative: string) => string
    relativeJustNow: string
    relativeThreeHoursAgo: string
    revokeAction: string
    revokeLabel: string
  }
  apiTokens: {
    title: string
    description: string
    createAction: string
    createLabel: string
    emptyState: string
  }
  // Real-engine Security (change password + live sessions).
  real: {
    pageTitle: string
    pageDescription: string
    passwordChanged: string
    otherDevicesSignedOut: string
    currentPasswordIncorrect: string
    couldNotChangePassword: string
    changeFailed: string
    sessionRevoked: string
    deviceSignedOut: string
    changePasswordTitle: string
    changePasswordHint: string
    currentPassword: string
    newPassword: string
    confirmNewPassword: string
    passwordsDoNotMatch: string
    saving: string
    updatePassword: string
    sessionsTitle: string
    sessionsDescription: string
    loading: string
    loadError: string
    empty: string
    unknownIp: string
    since: (when: string) => string
    revoke: string
    unknownDevice: string
    unknownOs: string
    browser: string
    twoFactorAndTokens: string
    comingSoon: string
  }
}

export const securityI18n: LocalizedDict<SecurityStrings> = {
  en: {
    pageTitle: 'Security',
    pageDescription:
      'Two-factor authentication, active sessions, and API tokens. Mock UI — real auth M3 Stage 4 SPEC.md Faz 1 (iam-auth + audit_events).',
    footerNote:
      'Placeholder section · backend binding pending M3 Stage 4 SPEC.md Faz 1 (iam-auth foundational + audit_events).',
    toastTail:
      'Real auth wires M3 Stage 4 SPEC.md Faz 1 (iam-auth foundational).',
    twoFactor: {
      title: 'Two-factor authentication',
      description:
        'Add a verification code on top of your password at sign-in.',
      disabledBadge: 'Disabled',
      enableAction: 'Enable 2FA',
      enableLabel: 'Enable 2FA',
    },
    sessions: {
      title: 'Active sessions',
      description:
        'Browsers and devices currently signed in to your account.',
      activeBadge: 'Active',
      currentSessionMeta: 'Current session',
      lastActive: (relative) => `Last active ${relative}`,
      relativeJustNow: 'just now',
      relativeThreeHoursAgo: '3 hours ago',
      revokeAction: 'Revoke',
      revokeLabel: 'Revoke session',
    },
    apiTokens: {
      title: 'API tokens',
      description: 'Personal access tokens for programmatic integrations.',
      createAction: 'Create token',
      createLabel: 'Create token',
      emptyState:
        'No tokens yet. Tokens will be issued via M3 iam-auth token endpoint.',
    },
    real: {
      pageTitle: 'Security',
      pageDescription: 'Manage your password and active sessions.',
      passwordChanged: 'Password changed',
      otherDevicesSignedOut: 'Other devices were signed out.',
      currentPasswordIncorrect: 'Current password is incorrect.',
      couldNotChangePassword: 'Could not change password.',
      changeFailed: 'Change failed',
      sessionRevoked: 'Session revoked',
      deviceSignedOut: 'The device was signed out.',
      changePasswordTitle: 'Change password',
      changePasswordHint: 'Changing your password signs out your other devices.',
      currentPassword: 'Current password',
      newPassword: 'New password (min 6)',
      confirmNewPassword: 'Confirm new password',
      passwordsDoNotMatch: 'Passwords do not match.',
      saving: 'Saving…',
      updatePassword: 'Update password',
      sessionsTitle: 'Active sessions',
      sessionsDescription: 'Devices with an active refresh session.',
      loading: 'Loading…',
      loadError: 'Could not load sessions.',
      empty: 'No active sessions.',
      unknownIp: 'unknown IP',
      since: (when) => `since ${when}`,
      revoke: 'Revoke',
      unknownDevice: 'Unknown device',
      unknownOs: 'Unknown OS',
      browser: 'Browser',
      twoFactorAndTokens: 'Two-factor authentication and API tokens',
      comingSoon: 'Coming soon',
    },
  },
  tr: {
    pageTitle: 'Güvenlik',
    pageDescription:
      'İki faktörlü kimlik doğrulama, aktif oturumlar ve API tokenları. Mock UI — gerçek kimlik doğrulama M3 Stage 4 SPEC.md Faz 1 (iam-auth + audit_events).',
    footerNote:
      'Yer tutucu bölüm · backend bağlama M3 Stage 4 SPEC.md Faz 1 (iam-auth temel + audit_events) ile gelir.',
    toastTail:
      'Gerçek kimlik doğrulama M3 Stage 4 SPEC.md Faz 1 (iam-auth temel) ile bağlanır.',
    twoFactor: {
      title: 'İki faktörlü kimlik doğrulama',
      description:
        'Giriş sırasında şifrenin üstüne bir doğrulama kodu ekle.',
      disabledBadge: 'Devre dışı',
      enableAction: '2FA aç',
      enableLabel: '2FA aç',
    },
    sessions: {
      title: 'Aktif oturumlar',
      description:
        'Hesabınla şu anda giriş yapmış tarayıcılar ve cihazlar.',
      activeBadge: 'Aktif',
      currentSessionMeta: 'Mevcut oturum',
      lastActive: (relative) => `Son aktiflik ${relative}`,
      relativeJustNow: 'az önce',
      relativeThreeHoursAgo: '3 saat önce',
      revokeAction: 'İptal et',
      revokeLabel: 'Oturumu iptal et',
    },
    apiTokens: {
      title: 'API tokenları',
      description:
        'Programatik entegrasyonlar için kişisel erişim tokenları.',
      createAction: 'Token oluştur',
      createLabel: 'Token oluştur',
      emptyState:
        'Henüz token yok. Tokenlar M3 iam-auth token endpoint üzerinden çıkacak.',
    },
    real: {
      pageTitle: 'Güvenlik',
      pageDescription: 'Parolanı ve aktif oturumlarını yönet.',
      passwordChanged: 'Parola değiştirildi',
      otherDevicesSignedOut: 'Diğer cihazlardan çıkış yapıldı.',
      currentPasswordIncorrect: 'Mevcut parola yanlış.',
      couldNotChangePassword: 'Parola değiştirilemedi.',
      changeFailed: 'Değişiklik başarısız',
      sessionRevoked: 'Oturum kapatıldı',
      deviceSignedOut: 'Cihazdan çıkış yapıldı.',
      changePasswordTitle: 'Parolayı değiştir',
      changePasswordHint: 'Parolanı değiştirince diğer cihazlarından çıkış yapılır.',
      currentPassword: 'Mevcut parola',
      newPassword: 'Yeni parola (en az 6)',
      confirmNewPassword: 'Yeni parolayı doğrula',
      passwordsDoNotMatch: 'Parolalar eşleşmiyor.',
      saving: 'Kaydediliyor…',
      updatePassword: 'Parolayı güncelle',
      sessionsTitle: 'Aktif oturumlar',
      sessionsDescription: 'Oturumu açık olan cihazlar.',
      loading: 'Yükleniyor…',
      loadError: 'Oturumlar yüklenemedi.',
      empty: 'Aktif oturum yok.',
      unknownIp: 'bilinmeyen IP',
      since: (when) => `${when} tarihinden beri`,
      revoke: 'Kapat',
      unknownDevice: 'Bilinmeyen cihaz',
      unknownOs: 'Bilinmeyen işletim sistemi',
      browser: 'Tarayıcı',
      twoFactorAndTokens: 'İki faktörlü doğrulama ve API tokenları',
      comingSoon: 'Yakında',
    },
  },
}
